// 全局状态管理 - 商业级SaaS数据共享
import { useState, useEffect, useCallback } from 'react';
import { AccountInfo } from '../utils/generators';
import * as historyApi from '../services/historyApi';

export interface CardDetails {
  format: string;
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

export interface HistoryItem {
  id: string;
  timestamp: string;
  cardNumber: string;
  expiry: string;
  cvv: string;
  cardType: string;
  accountInfo?: AccountInfo;
}

export interface AppState {
  // 卡号段生成数据
  cardGeneration: {
    cardNumber: string;
    accountInfo: AccountInfo;
    generatedCard: string;
    generatedExpiry: string;
    generatedCVV: string;
    lastSaved: string | null;
  };

  // 完整卡信息数据
  cardInformation: {
    cardDetails: CardDetails;
    lastSaved: string | null;
  };

  // 生成历史记录
  history: HistoryItem[];
  historyLoading: boolean;
  historyError: string | null;

  // 全局设置
  settings: {
    autoSave: boolean;
    theme: 'light' | 'dark';
    language: 'zh' | 'en';
    useCloudSync: boolean;
  };

  // 用户偏好
  preferences: {
    defaultCountry: string;
    preferredCardType: string;
    saveHistory: boolean;
  };
}

// 默认状态
const defaultState: AppState = {
  cardGeneration: {
    cardNumber: '62283663xxxxxxxx',
    accountInfo: {
      holderName: '',
      country: '中国',
      countryCode: 'CN',
      province: '',
      city: '',
      district: '',
      postalCode: '',
      street: '',
      addressLine2: '',
    },
    generatedCard: '',
    generatedExpiry: '',
    generatedCVV: '',
    lastSaved: null,
  },
  cardInformation: {
    cardDetails: {
      format: '',
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
    },
    lastSaved: null,
  },
  history: [],
  historyLoading: false,
  historyError: null,
  settings: {
    autoSave: true,
    theme: 'light',
    language: 'zh',
    useCloudSync: true,
  },
  preferences: {
    defaultCountry: '中国',
    preferredCardType: 'visa',
    saveHistory: true,
  },
};

const MAX_HISTORY_ITEMS = 100;

// 存储管理类
class AppStore {
  private state: AppState;
  private listeners: Array<(state: AppState) => void> = [];
  private storageKey = 'bankCardAppStore';

  constructor() {
    this.state = this.loadFromStorage();
    this.applyTheme();
  }

  // 从localStorage加载状态
  private loadFromStorage(): AppState {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaultState,
          ...parsed,
          history: parsed.history || [],
          historyLoading: false,
          historyError: null,
          settings: { ...defaultState.settings, ...parsed.settings },
          preferences: { ...defaultState.preferences, ...parsed.preferences },
        };
      }
    } catch (error) {
      console.error('Failed to load app state:', error);
    }
    return defaultState;
  }

  // 保存状态到localStorage
  private saveToStorage(): void {
    try {
      // 不保存 loading 和 error 状态
      const stateToSave = {
        ...this.state,
        historyLoading: false,
        historyError: null,
      };
      localStorage.setItem(this.storageKey, JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Failed to save app state:', error);
    }
  }

  // 应用主题
  private applyTheme(): void {
    const isDark = this.state.settings.theme === 'dark';
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  // 获取当前状态
  getState(): AppState {
    return { ...this.state };
  }

  // 订阅状态变化
  subscribe(listener: (state: AppState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // 通知所有监听器
  private notify(): void {
    this.listeners.forEach((listener) => listener(this.state));
  }

  // 更新卡号段生成数据
  updateCardGeneration(updates: Partial<AppState['cardGeneration']>): void {
    this.state = {
      ...this.state,
      cardGeneration: {
        ...this.state.cardGeneration,
        ...updates,
        lastSaved: new Date().toISOString(),
      },
    };

    if (this.state.settings.autoSave) {
      this.saveToStorage();
    }

    this.notify();
  }

  // 更新完整卡信息数据
  updateCardInformation(updates: Partial<AppState['cardInformation']>): void {
    this.state = {
      ...this.state,
      cardInformation: {
        ...this.state.cardInformation,
        ...updates,
        lastSaved: new Date().toISOString(),
      },
    };

    if (this.state.settings.autoSave) {
      this.saveToStorage();
    }

    this.notify();
  }

  // 从云端加载历史记录
  async loadHistoryFromCloud(): Promise<void> {
    if (!this.state.settings.useCloudSync) return;

    this.state = { ...this.state, historyLoading: true, historyError: null };
    this.notify();

    try {
      const history = await historyApi.fetchHistory();
      this.state = {
        ...this.state,
        history,
        historyLoading: false,
      };
      this.saveToStorage();
    } catch (error) {
      this.state = {
        ...this.state,
        historyLoading: false,
        historyError: '加载历史记录失败',
      };
    }
    this.notify();
  }

  // 添加历史记录
  async addHistory(item: Omit<HistoryItem, 'id' | 'timestamp'>): Promise<void> {
    if (!this.state.preferences.saveHistory) return;

    // 先本地添加（乐观更新）
    const localItem: HistoryItem = {
      ...item,
      id: Date.now().toString(36) + Math.random().toString(36).substr(2),
      timestamp: new Date().toISOString(),
    };

    this.state = {
      ...this.state,
      history: [localItem, ...this.state.history].slice(0, MAX_HISTORY_ITEMS),
    };
    this.saveToStorage();
    this.notify();

    // 如果启用云同步，同步到云端
    if (this.state.settings.useCloudSync) {
      try {
        const cloudItem = await historyApi.addHistoryItem(item);
        if (cloudItem) {
          // 用云端返回的 item 替换本地的
          this.state = {
            ...this.state,
            history: this.state.history.map((h) =>
              h.id === localItem.id ? cloudItem : h
            ),
          };
          this.saveToStorage();
          this.notify();
        }
      } catch (error) {
        console.error('Failed to sync history to cloud:', error);
      }
    }
  }

  // 删除历史记录
  async deleteHistory(id: string): Promise<void> {
    // 先本地删除
    this.state = {
      ...this.state,
      history: this.state.history.filter((item) => item.id !== id),
    };
    this.saveToStorage();
    this.notify();

    // 云端同步
    if (this.state.settings.useCloudSync) {
      await historyApi.deleteHistoryItem(id);
    }
  }

  // 清空历史记录
  async clearHistory(): Promise<void> {
    this.state = {
      ...this.state,
      history: [],
    };
    this.saveToStorage();
    this.notify();

    if (this.state.settings.useCloudSync) {
      await historyApi.clearAllHistory();
    }
  }

  // 更新设置
  updateSettings(updates: Partial<AppState['settings']>): void {
    this.state = {
      ...this.state,
      settings: {
        ...this.state.settings,
        ...updates,
      },
    };

    if (updates.theme) {
      this.applyTheme();
    }

    this.saveToStorage();
    this.notify();
  }

  // 切换主题
  toggleTheme(): void {
    const newTheme = this.state.settings.theme === 'light' ? 'dark' : 'light';
    this.updateSettings({ theme: newTheme });
  }

  // 更新偏好设置
  updatePreferences(updates: Partial<AppState['preferences']>): void {
    this.state = {
      ...this.state,
      preferences: {
        ...this.state.preferences,
        ...updates,
      },
    };

    this.saveToStorage();
    this.notify();
  }

  // 手动保存
  save(): void {
    this.saveToStorage();
  }

  // 重置所有数据
  reset(): void {
    this.state = defaultState;
    localStorage.removeItem(this.storageKey);
    this.applyTheme();
    this.notify();
  }

  // 清除指定模块数据
  clearModule(module: 'cardGeneration' | 'cardInformation'): void {
    if (module === 'cardGeneration') {
      this.state.cardGeneration = defaultState.cardGeneration;
    } else if (module === 'cardInformation') {
      this.state.cardInformation = defaultState.cardInformation;
    }

    this.saveToStorage();
    this.notify();
  }

  // 导出数据
  exportData(): string {
    return JSON.stringify(this.state, null, 2);
  }

  // 导入数据
  importData(jsonString: string): boolean {
    try {
      const imported = JSON.parse(jsonString);
      this.state = { ...defaultState, ...imported };
      this.saveToStorage();
      this.applyTheme();
      this.notify();
      return true;
    } catch (error) {
      console.error('Failed to import data:', error);
      return false;
    }
  }
}

// 创建全局store实例
export const appStore = new AppStore();

// React Hook for using the store
export function useAppStore(): [AppState, typeof appStore] {
  const [state, setState] = useState(appStore.getState());

  useEffect(() => {
    const unsubscribe = appStore.subscribe(setState);
    return unsubscribe;
  }, []);

  // 初始化时从云端加载历史
  const loadHistory = useCallback(() => {
    appStore.loadHistoryFromCloud();
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  return [state, appStore];
}

// 获取格式化的最后保存时间
export function getLastSavedText(timestamp: string | null): string {
  if (!timestamp) return '未保存';

  const date = new Date(timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

  if (diffInMinutes < 1) return '刚刚保存';
  if (diffInMinutes < 60) return `${diffInMinutes}分钟前保存`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}小时前保存`;

  return date.toLocaleDateString();
}

// 格式化历史记录时间
export function formatHistoryTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

  if (diffInMinutes < 1) return '刚刚';
  if (diffInMinutes < 60) return `${diffInMinutes}分钟前`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}小时前`;
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}天前`;

  return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
}
